import store from '@/store/store';
import { actions as _act } from '@/models/store.actions';
import { isNativeApp } from '@/helpers/platform-helper';
import { EuSignNativeProxyService } from '@/sign/services/eusign-native-proxy.service';
import { logger } from '@/helpers/log.service';
import { CloudProvider, DepositSignAuthType, PrivateKeyType, SignMethod, CloudKeyReaderType } from '@/sign/models/sign-constants';
import { euSignFilesService } from './eusign-files.service';
import { appSettingsHelper } from '@/helpers/settings-helper';

let euscp: any;
const libraries = new Map();
const librariesInitPromises = new Map();

class EuSignService {
  private _signLibFilesClientVersion?: string;

  private get appHost(): string {
    return isNativeApp() ? store.getters[_act.SETT_GET_APIHOSTGET] : window.location.origin;
  }

  private get apiUrl(): string {
    return store.getters[_act.SETT_GET_APIURLGET];
  }

  private async getEuSettings(): Promise<any> {
    const cas = await euSignFilesService.getCertifyingAuthorities();
    const caCertificates = await euSignFilesService.getCaCertificates();
    const appSett = store.getters[_act.SETT_GET_APPSET];
    let directAccess = true;
    let ocspResponseExpireTime = 30;
    if (appSett && appSett.euSign) {
      if (appSett.euSign.directAccess === false) {
        directAccess = false;
      }
      if ('ocspResponseExpireTime' in appSett.euSign) {
        ocspResponseExpireTime = +appSett.euSign.ocspResponseExpireTime;
      }
    }
    const res = {
      language: 'uk',
      encoding: 'utf-8',
      httpProxyServiceURL: new URL(this.apiUrl + 'proxyhandler', this.appHost).href,
      directAccess: directAccess,
      CAs: cas,
      CACertificates: caCertificates,
      ocspResponseExpireTime: ocspResponseExpireTime
    };
    return res;
  }

  public getLibrary(
    signMethod: SignMethod, privateKeyType: PrivateKeyType,
    signProvider: string = '', keyReaderType: string = ''): any {
    return libraries.get(this.getLibraryName(signMethod, privateKeyType, signProvider, keyReaderType));
  }

  public getLibraryFirst(): any {
    return libraries.values().next().value;
  }

  public async init(
    signMethod: SignMethod, privateKeyType: PrivateKeyType,
    signProvider: string = '', keyReaderType: string = ''): Promise<void> {
    logger.debug(`[EuSignService:init] signMethod="${signMethod}" privateKeyType="${privateKeyType}" signProvider="${signProvider}" keyReaderType="${keyReaderType}"`);

    const libName = this.getLibraryName(signMethod, privateKeyType, signProvider, keyReaderType);
    let initPromiseResolve: (value?: unknown) => void = () => {};
    let initPromiseReject: (reason?: any) => void = () => {};
    let createPromiseFn = () => new Promise((resolve, reject) => {
      initPromiseResolve = resolve;
      initPromiseReject = reject;
    });

    if (librariesInitPromises.has(libName)) {
      await librariesInitPromises.get(libName);
    }

    librariesInitPromises.set(libName, createPromiseFn());

    try {
      await this.import();

      switch (signMethod) {
        case SignMethod.FileSystem:
          await this.initFileSystemLibs(privateKeyType);
          break;
        case SignMethod.Device:
          await this.initDeviceLibs(privateKeyType);
          break;
        case SignMethod.Cloud:
          await this.initCloudLibs(privateKeyType, signProvider, keyReaderType);
          break;
        default:
          throw new Error(`Method ${signMethod} is not supported`);
      }

      this._signLibFilesClientVersion = store.getters[_act.SETTTEMP_GET_SIGNLIBFILESSERVERVERSION];

      initPromiseResolve();
    } catch (error) {
      initPromiseReject(error);
      librariesInitPromises.delete(libName);
      throw error;
    }
  }

  public isReinitializationRequired(): boolean {
    const serverVersion = store.getters[_act.SETTTEMP_GET_SIGNLIBFILESSERVERVERSION];
    const isReinitializationRequired = !!serverVersion && !!this._signLibFilesClientVersion && this._signLibFilesClientVersion !== serverVersion;
    logger.debug(`[EuSignService] isReinitializationRequired = ${isReinitializationRequired}`);
    return isReinitializationRequired;
  }

  public reInit(): void {
    libraries.clear();

    euSignFilesService.clearCache();

    this._signLibFilesClientVersion = undefined;
  }

  private getLibraryName(
    signMethod: SignMethod, privateKeyType: PrivateKeyType,
    signProvider: string = '', keyReaderType: string = ''): string {
    let name = `${signMethod}`;
    if (signMethod === SignMethod.Device && isNativeApp()) {
      name += `-${privateKeyType}`;
    }
    if (signMethod === SignMethod.Cloud &&
        !(
          (signProvider === CloudProvider.DepositSign && keyReaderType === DepositSignAuthType.LoginPassword) ||
          (signProvider === CloudProvider.CloudKey && keyReaderType === CloudKeyReaderType.FileSystem) ||
          signProvider === CloudProvider.DPDiya
        )
    ) {
      name += `-${privateKeyType}-${signProvider}-${keyReaderType}`;
    }
    return name;
  }

  private async import(): Promise<void> {
    if (!euscp) {
      euscp = await import(/* webpackChunkName: "euscp" */ 'euscp');
    }
  }

  private async initFileSystemLibs(privateKeyType: PrivateKeyType): Promise<void> {
    await this.initFileSystem(privateKeyType);
  }

  private async initFileSystem(privateKeyType: PrivateKeyType): Promise<void> {
    const libKey = this.getLibraryName(SignMethod.FileSystem, privateKeyType);
    let lib = libraries.get(libKey);
    if (!lib) {
      logger.debug(`[EuSignService:initFileSystem] initializing library key="${libKey}"`);
      lib = new euscp.EndUser('', euscp.EndUserConstants.EU_LIBRARY_TYPE_JS);
      const euSettings = await this.getEuSettings();
      await lib.Initialize(euSettings);
      await lib.SetRuntimeParameter(
        euscp.EndUserConstants.EU_SIGN_TYPE_PARAMETER,
        euscp.EndUserConstants.EU_SIGN_TYPE_CADES_X_LONG | euscp.EndUserConstants.EU_SIGN_TYPE_CADES_X_LONG_TRUSTED);
      libraries.set(libKey, lib);
    }
  }

  private async initDeviceLibs(privateKeyType: PrivateKeyType): Promise<void> {
    await this.initDevice(privateKeyType);
  }

  private async initDevice(privateKeyType: PrivateKeyType): Promise<void> {
    let libInfo;
    const libKey = this.getLibraryName(SignMethod.Device, privateKeyType);
    let lib = libraries.get(libKey);
    if (!lib) {
      logger.debug(`[EuSignService:initDevice] initializing library key="${libKey}"`);
      try {
        if (isNativeApp()) {
          lib = new EuSignNativeProxyService();
        } else {
          lib = new euscp.EndUser('', euscp.EndUserConstants.EU_LIBRARY_TYPE_SW);
          libInfo = await lib.GetLibraryInfo();
          const euSettings = await this.getEuSettings();
          await lib.Initialize(euSettings);
          await lib.SetRuntimeParameter(
            euscp.EndUserConstants.EU_SIGN_TYPE_PARAMETER,
            euscp.EndUserConstants.EU_SIGN_TYPE_CADES_X_LONG | euscp.EndUserConstants.EU_SIGN_TYPE_CADES_X_LONG_TRUSTED);
        }
        libraries.set(libKey, lib);
      } catch (err) {
        if (err && libInfo) {
          (err as any).libInfo = libInfo;
        }
        throw err;
      }
    }
  }

  private async initCloudLibs(privateKeyType: PrivateKeyType, signProvider: string = '', keyReaderType: string = ''): Promise<void> {
    const libKey = this.getLibraryName(SignMethod.Cloud, privateKeyType, signProvider, keyReaderType);
    let lib = libraries.get(libKey);
    if (!lib) {
      logger.debug(`[EuSignService:initCloudLibs] initializing library key="${libKey}"`);

      let libType = `${signProvider}-${keyReaderType}`;
      switch (libType) {
        case `${CloudProvider.DepositSign}-${DepositSignAuthType.LoginPassword}`:
        case `${CloudProvider.DPDiya}-`:
        case `${CloudProvider.CloudKey}-${CloudKeyReaderType.FileSystem}`:
          await this.initCloudLibDefault(libKey);
          break;
        default:
          await this.initIitCloudProviderLib(libKey, signProvider);
          break;
      }
    }
  }

  private async initCloudLibDefault(libKey: string = ''): Promise<void> {
    const lib = new euscp.EndUser('', euscp.EndUserConstants.EU_LIBRARY_TYPE_JS);
    const euSettings = await this.getEuSettings();
    await lib.Initialize(euSettings);
    libraries.set(libKey, lib);
  }

  private async initIitCloudProviderLib(libKey: string, cloudProvider: string): Promise<void> {
    const lib = new euscp.EndUser('', euscp.EndUserConstants.EU_LIBRARY_TYPE_JS);
    const euSettings = await this.getEuSettings();
    const sett = Object.assign({}, euSettings);

    const cloudProviderSettings = appSettingsHelper.iitCloudProviderSettings
      .find(p => p.id === cloudProvider);

    logger.debug(`[EuSignService:initIitCloudProviderLib] cloudProviderSettings`, cloudProviderSettings);

    if (!cloudProviderSettings) {
      throw new Error('Cloud provider settings not found');
    }

    sett.KSPs = [
      {
        name: cloudProviderSettings.name,
        ksp: cloudProviderSettings.ksp,
        address: cloudProviderSettings.address,
        port: cloudProviderSettings.port,
        directAccess: cloudProviderSettings.directAccess,
        clientIdPrefix: cloudProviderSettings.clientIdPrefix,
        confirmationURL: cloudProviderSettings.confirmationUrl,
        mobileAppName: cloudProviderSettings.mobileAppName
      }
    ];

    await lib.Initialize(sett);
    await lib.SetRuntimeParameter(
      euscp.EndUserConstants.EU_SIGN_TYPE_PARAMETER,
      euscp.EndUserConstants.EU_SIGN_TYPE_CADES_X_LONG | euscp.EndUserConstants.EU_SIGN_TYPE_CADES_X_LONG_TRUSTED);
    libraries.set(libKey, lib);
  }
}

const euSignService = new EuSignService();

export { euSignService };
